import React, { useState, useEffect } from 'react';
import { Card, Typography } from 'antd';
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useCommonStore } from '@/store/useCommonStore';
import { Lunar } from 'lunar-javascript';

const { Title } = Typography;

const YearProgressClock = () => {
  const [currentTime, setCurrentTime] = useState('');
  const [percentage, setPercentage] = useState(0);
  const [dayOfYear, setDayOfYear] = useState(0);
  const [daysLeft, setDaysLeft] = useState(365);
  const [lunarYear, setLunarYear] = useState('');
  
  // 获取主题色
  const primaryColor = useCommonStore(state => state.primaryColor || '#1890ff');

  // 更新时间和进度
  const updateTimeAndProgress = () => {
    const now = new Date();
    const year = now.getFullYear();
    
    // 格式化时间显示
    const timeString = now.toLocaleTimeString('zh-CN', { hour12: false });
    setCurrentTime(timeString);
    
    // 获取农历年份
    const lunar = Lunar.fromDate(now);
    const lunarYearName = lunar.getYearInGanZhi(); // 获取干支年份，如"甲辰"
    setLunarYear(lunarYearName);
    
    // 计算一年中的第几天
    const start = new Date(year, 0, 1);
    const diff = now - start;
    const oneDay = 1000 * 60 * 60 * 24;
    const currentDayOfYear = Math.floor(diff / oneDay) + 1;
    
    // 计算百分比
    const currentPercentage = ((currentDayOfYear / 365) * 100).toFixed(2);
    
    // 更新状态
    setPercentage(currentPercentage);
    setDayOfYear(currentDayOfYear);
    setDaysLeft(365 - currentDayOfYear);
  };
  
  useEffect(() => {
    // 初始更新
    updateTimeAndProgress();
    
    // 每秒更新
    const interval = setInterval(updateTimeAndProgress, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // 获取当前日期
  const getCurrentDate = () => {
    const now = new Date();
    const options = { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' };
    return now.toLocaleDateString('zh-CN', options);
  };

  return (
    <Card className="overflow-hidden w-full" variant="borderless">
      <div className="flex items-center justify-between mb-6">
        <Title level={4} className="mb-0">
          <CalendarOutlined className="mr-2" />{new Date().getFullYear()}年 ({lunarYear}年)
        </Title>
        <div className="text-right">
          <div className="text-sm text-gray-500">{getCurrentDate()}</div>
          <div className="text-lg font-mono" style={{ color: primaryColor }}>
            <ClockCircleOutlined className="mr-1" />{currentTime}
          </div>
        </div>
      </div>
      
      <div className="space-y-6">
        {/* 进度条 */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">年度进度</span>
            <span className="text-2xl font-bold" style={{ color: primaryColor }}>{percentage}%</span>
          </div>
          <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-1000 ease-out"
              style={{ 
                width: `${percentage}%`, 
                backgroundColor: primaryColor,
                boxShadow: `0 0 10px ${primaryColor}40`
              }}
            ></div>
          </div>
        </div>
        
        {/* 统计信息 */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold" style={{ color: primaryColor }}>{dayOfYear}</div>
            <div className="text-sm text-gray-600">已过去天数</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-600">{daysLeft}</div>
            <div className="text-sm text-gray-600">剩余天数</div>
          </div>
        </div>
        
        {/* 励志语录 */}
        <div className="text-center text-gray-500 italic text-sm pt-4 border-t border-gray-100">
          "时间不在于拥有多少，而在于怎样使用。"
        </div>
      </div>
    </Card>
  );
};

export default YearProgressClock;